/**
 * WorkerVersionsPanel — collapsible "Version history" widget for WorkerDetail.
 *
 * Lists the most recent worker versions, lets the admin revert to any of them.
 * On revert, the current state is auto-snapshotted first, so revert is itself
 * reversible.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import {
  CaretDown,
  ClockCounterClockwise,
  ArrowUUpLeft,
  CircleNotch,
} from '@phosphor-icons/react';

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

const fieldsChanged = (snapshot) => {
  if (!snapshot) return [];
  return Object.keys(snapshot).filter((k) => snapshot[k] !== undefined && snapshot[k] !== null);
};

export const WorkerVersionsPanel = ({ workerId, orgId, onReverted }) => {
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reverting, setReverting] = useState(null);

  const load = useCallback(async () => {
    if (!workerId) return;
    setLoading(true);
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/workers/${workerId}/versions`, {}, orgId);
      if (res.ok) {
        const data = await res.json();
        setVersions(data.versions || []);
      }
    } catch {}
    setLoading(false);
  }, [workerId, orgId]);

  useEffect(() => {
    if (open && versions.length === 0) load();
  }, [open, versions.length, load]);

  const handleRevert = async (version) => {
    if (!window.confirm(`Revert this worker to version ${version}? Your current configuration will be saved as a new version first.`)) {
      return;
    }
    setReverting(version);
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/workers/${workerId}/revert/${version}`, {
        method: 'POST',
      }, orgId);
      if (res.ok) {
        toast.success(`Reverted to v${version}`);
        await load();
        if (onReverted) onReverted();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not revert.');
      }
    } catch {
      toast.error('Could not revert.');
    }
    setReverting(null);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.24 }}
      className="rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#1E2836] mb-5 shadow-[0_1px_3px_rgba(0,0,0,0.14),0_6px_20px_rgba(0,0,0,0.10)]"
      data-testid="worker-versions-section"
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left"
        data-testid="versions-toggle"
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[rgba(99,102,241,0.08)] flex items-center justify-center">
            <ClockCounterClockwise size={16} className="text-[#818CF8]" weight="duotone" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#F0F4F8]">Version history</p>
            <p className="text-[11px] text-[#7D8896]">
              Auto-saved snapshots — revert any change
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {versions.length > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[rgba(255,255,255,0.03)] text-[#7D8896]">
              {versions.length} version{versions.length !== 1 ? 's' : ''}
            </span>
          )}
          <CaretDown
            size={16}
            className={`text-[#7D8896] transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5" data-testid="versions-content">
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <CircleNotch size={18} className="animate-spin text-[#818CF8]" />
            </div>
          ) : versions.length === 0 ? (
            <div className="text-center py-6 text-xs text-[#7D8896]">
              No saved versions yet. Versions are auto-saved every time you change this worker.
            </div>
          ) : (
            <div className="space-y-2" data-testid="versions-list">
              {versions.map((v, idx) => (
                <div
                  key={v.version}
                  data-testid={`version-row-${v.version}`}
                  className="flex items-center justify-between bg-[#111827] border border-[rgba(255,255,255,0.06)] rounded-lg px-3 py-2.5"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-[#F0F4F8]">v{v.version}</span>
                      {idx === 0 && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-[rgba(0,229,192,0.08)] text-[#00E5C0]">
                          latest
                        </span>
                      )}
                      <span className="text-[10px] text-[#7D8896] capitalize">{v.source || 'agent'}</span>
                    </div>
                    <p className="text-[10px] text-[#5C6778] mt-0.5">
                      {formatTime(v.created_at)}
                      {v.change_summary && v.change_summary !== 'auto-snapshot' && (
                        <span className="text-[#818CF8] ml-2">· {v.change_summary}</span>
                      )}
                    </p>
                    {v.snapshot && (
                      <p className="text-[10px] text-[#7D8896] mt-0.5 line-clamp-1">
                        Captured: {fieldsChanged(v.snapshot).slice(0, 6).join(', ')}
                        {fieldsChanged(v.snapshot).length > 6 ? '…' : ''}
                      </p>
                    )}
                  </div>
                  {idx > 0 && (
                    <button
                      onClick={() => handleRevert(v.version)}
                      disabled={!!reverting}
                      data-testid={`revert-version-${v.version}-btn`}
                      className="ml-3 flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-medium rounded-md border border-[rgba(129,140,248,0.18)] text-[#818CF8] hover:bg-[rgba(129,140,248,0.06)] transition-colors disabled:opacity-50 shrink-0"
                    >
                      {reverting === v.version ? (
                        <CircleNotch size={11} className="animate-spin" />
                      ) : (
                        <ArrowUUpLeft size={11} weight="bold" />
                      )}
                      Revert
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default WorkerVersionsPanel;
